import { useMemo, useSyncExternalStore } from "react";
import type { MapRouting } from "../shared/types";

type RoutingUpdate = MapRouting | ((current: MapRouting) => MapRouting);
interface RoutingHistorySnapshot {
  routing: MapRouting;
  canUndo: boolean;
  canRedo: boolean;
}
interface RoutingHistoryOptions {
  maxEntries?: number;
  maxHistoryBytes?: number;
}
interface Entry {
  routing: MapRouting;
  text: string;
  bytes: number;
}

/** Dialog-local history: transactions group drags or field edits, not camera moves. */
export function createRoutingHistory(
  initial: MapRouting,
  options: RoutingHistoryOptions = {},
) {
  const maxEntries = Math.max(0, options.maxEntries ?? 40);
  const maxHistoryBytes = Math.max(
    0,
    options.maxHistoryBytes ?? 8 * 1_024 * 1_024,
  );
  const entry = (
    routing: MapRouting,
    text = JSON.stringify(routing),
  ): Entry => ({
    // Own each state so caller mutations cannot rewrite previous history.
    routing: structuredClone(routing),
    text,
    bytes: text.length * 2,
  });
  let current = entry(initial);
  let before: Entry | null = null;
  let past: Entry[] = [];
  let future: Entry[] = [];
  const listeners = new Set<() => void>();
  let snapshot: RoutingHistorySnapshot = {
    routing: current.routing,
    canUndo: false,
    canRedo: false,
  };
  const changedGroup = () => before !== null && before.text !== current.text;
  const publish = () => {
    const canUndo = changedGroup() || past.length > 0;
    const canRedo = !changedGroup() && future.length > 0;
    if (
      snapshot.routing === current.routing &&
      snapshot.canUndo === canUndo &&
      snapshot.canRedo === canRedo
    )
      return;
    snapshot = { routing: current.routing, canUndo, canRedo };
    listeners.forEach((listener) => listener());
  };
  const trim = () => {
    let bytes = [...past, ...future].reduce((sum, item) => sum + item.bytes, 0);
    while (
      past.length + future.length > maxEntries ||
      bytes > maxHistoryBytes
    ) {
      // Keep the nearest undo/redo states; discard the oldest undo first.
      const removed = past.length ? past.shift() : future.shift();
      if (!removed) break;
      bytes -= removed.bytes;
    }
  };
  const begin = () => {
    before ??= current;
  };
  const end = () => {
    if (before && before.text !== current.text) {
      past.push(before);
      future = [];
      trim();
    }
    before = null;
    publish();
    return current.routing;
  };
  const update = (next: RoutingUpdate) => {
    const routing = typeof next === "function" ? next(current.routing) : next;
    const text = JSON.stringify(routing);
    if (text === current.text) return current.routing;
    if (!before) {
      past.push(current);
      future = [];
    }
    current = entry(routing, text);
    if (!before) trim();
    publish();
    return current.routing;
  };
  const undo = () => {
    end();
    const previous = past.pop();
    if (previous) {
      future.push(current);
      current = previous;
      trim();
      publish();
    }
    return current.routing;
  };
  const redo = () => {
    end();
    const next = future.pop();
    if (next) {
      past.push(current);
      current = next;
      trim();
      publish();
    }
    return current.routing;
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    update,
    undo,
    redo,
    begin,
    end,
  };
}

export function useRoutingHistory(initial: MapRouting) {
  // The initial value belongs to this dialog instance. Parent rerenders must
  // not replace the editing session or its history.
  const history = useMemo(() => createRoutingHistory(initial), []);
  const snapshot = useSyncExternalStore(
    history.subscribe,
    history.getSnapshot,
    history.getSnapshot,
  );
  return {
    ...snapshot,
    update: history.update,
    undo: history.undo,
    redo: history.redo,
    begin: history.begin,
    end: history.end,
  };
}
